import { database } from './core';
import {
  ref,
  get,
  onValue,
  query,
  orderByChild,
  limitToLast
} from 'firebase/database';
import { createWithKey, updateData, removeData } from './api-wrapper.service';
import { getCurrentUser } from './auth.service';
// Notification creation is handled server-side by 'sendNewCommentNotification' Cloud Function
import { subscriptionManager } from './subscription-manager';
import logger from '../../utils/logger';

// Comment interface matching original app structure
export interface Comment {
  id: string;                     // Always required for UI operations (added by us)
  key?: string;                   // Original app uses key
  timestamp: number;              // Creation timestamp
  author: {                       // Author info
    uid: string;
    username: string;
    isPeakSub?: boolean;          // Peak subscription status
    isInstructor?: boolean;       // Instructor status
  };
  content: string;                // Comment text
  post: {                         // Post reference
    title: string;
    type: string;
    owner: string;
  };
  isReported: boolean;
  // Optional fields for future features
  // Comment likes removed - not in original app
  // likes?: Record<string, boolean>;
  // likeCount?: number;
  isEdited?: boolean;
  editedAt?: number;
  // Shadowban indicator (added by useComments hook for admin UI)
  isShadowbanned?: boolean;
}

// Export types for backwards compatibility
export interface CommentInput {
  text: string;
  replyTo?: string;
}

export interface CommentQueryOptions {
  limit?: number;
  orderBy?: 'dateCreated' | 'likesCount';
  startAfter?: string;
}

/**
 * Adds a comment to a post - matches original app pattern
 * @param postKey The key/ID of the post being commented on
 * @param text The comment text
 * @param postDetails Optional post details (title, type, owner)
 * @returns Comment ID
 */
export const addComment = async (
  postKey: string,
  text: string,
  postDetails?: { title?: string; type?: string; owner?: string }
): Promise<string> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('Must be logged in to comment');
    }

    // Get user profile for author info
    const userProfileRef = ref(database, `users/${user.uid}`);
    const userSnapshot = await get(userProfileRef);
    const userProfile = userSnapshot.val();

    if (!userProfile) {
      throw new Error('User profile not found');
    }

    // Create comment data matching original app structure exactly
    const commentData = {
      timestamp: Date.now(),
      author: {
        uid: user.uid,
        username: userProfile.username || userProfile.displayName || 'User'
      },
      content: text.trim(),
      post: {
        title: postDetails?.title || '',
        type: postDetails?.type || 'post',
        owner: postDetails?.owner || ''
      },
      isReported: false
    };

    // Add comment to /comments/{postKey} path - matches original app
    // Use createWithKey which ensures database is online before writing
    const commentId = await createWithKey(`comments/${postKey}`, commentData);

    if (!commentId) {
      throw new Error('Failed to generate comment ID');
    }

    logger.info(`[comments.service] Comment added to post ${postKey}`);
    
    // Notification is handled by the 'sendNewCommentNotification' Cloud Function
    // which triggers on /comments/{postKey}/{commentKey} onWrite
    
    // The cloud function will handle:
    // - Updating comment count on the original post
    
    return commentId;
  } catch (error) {
    logger.error('Error adding comment:', error);
    throw error;
  }
};

/**
 * Updates a comment
 * @param postKey The post key
 * @param commentId The comment ID
 * @param newText The updated text
 */
export const updateComment = async (
  postKey: string,
  commentId: string,
  newText: string
): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('Must be logged in to update comment');
    }

    const commentRef = ref(database, `comments/${postKey}/${commentId}`);
    
    // Verify the user owns this comment
    const snapshot = await get(commentRef);
    if (!snapshot.exists()) {
      throw new Error('Comment not found');
    }

    const comment = snapshot.val();
    if (comment.author?.uid !== user.uid) {
      throw new Error('You can only edit your own comments');
    }

    // Update comment using api-wrapper which ensures database is online
    const trimmed = newText.trim();
    
    await updateData(`comments/${postKey}/${commentId}`, {
      content: trimmed,
      text: trimmed, // maintain compatibility with any new consumers
      isEdited: true,
      editedAt: Date.now()
    });

    logger.info(`[comments.service] Comment ${commentId} updated`);
  } catch (error) {
    logger.error('Error updating comment:', error);
    throw error;
  }
};

/**
 * Deletes a comment
 * @param postKey The post key
 * @param commentId The comment ID
 */
export const deleteComment = async (
  postKey: string,
  commentId: string
): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('Must be logged in to delete comment');
    }

    const commentRef = ref(database, `comments/${postKey}/${commentId}`);
    
    // Verify the user owns this comment
    const snapshot = await get(commentRef);
    if (!snapshot.exists()) {
      throw new Error('Comment not found');
    }

    const comment = snapshot.val();
    if (comment.author?.uid !== user.uid) {
      throw new Error('You can only delete your own comments');
    }

    // Delete comment using api-wrapper which ensures database is online
    await removeData(`comments/${postKey}/${commentId}`);

    logger.info(`[comments.service] Comment ${commentId} deleted`);
    
    // Cloud function will handle updating comment count
  } catch (error) {
    logger.error('Error deleting comment:', error);
    throw error;
  }
};

/**
 * Gets comments for a post
 * @param postKey The post key
 * @param limit Number of comments to fetch
 * @returns Array of comments
 */
export const getComments = async (
  postKey: string,
  limit: number = 50
): Promise<Comment[]> => {
  try {
    const commentsRef = ref(database, `comments/${postKey}`);
    const commentsQuery = query(
      commentsRef,
      orderByChild('timestamp'),
      limitToLast(limit)
    );

    const snapshot = await get(commentsQuery);
    
    logger.info(`[comments.service] Fetching comments for post ${postKey}`);
    logger.info(`[comments.service] Snapshot exists: ${snapshot.exists()}`);
    
    if (!snapshot.exists()) {
      logger.info(`[comments.service] No comments found for post ${postKey}`);
      return [];
    }

    const comments: Comment[] = [];
    snapshot.forEach((childSnapshot) => {
      const commentData = childSnapshot.val();
      logger.info(`[comments.service] Comment data:`, commentData);
      comments.push({
        id: childSnapshot.key!,
        key: childSnapshot.key!,  // Include key for compatibility
        ...commentData
      });
    });

    // Sort by date ascending (oldest first)
    comments.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    return comments;
  } catch (error) {
    logger.error('Error getting comments:', error);
    return [];
  }
};

/**
 * Subscribe to comments for a post with real-time updates
 * @param postKey The post key
 * @param callback Callback when comments change
 * @param limit Number of comments to fetch
 * @returns Unsubscribe function
 */
// Sequence counter appended to subscription-manager keys so two subscribers
// to the same post get unique keys (same pattern as api-wrapper.service.ts) -
// a shared `comments:${postKey}` key made the second subscriber overwrite the
// first's cleanup
let subscriptionSeq = 0;

export const subscribeToComments = (
  postKey: string,
  callback: (comments: Comment[]) => void,
  limit: number = 50
): (() => void) => {
  try {
    const commentsRef = ref(database, `comments/${postKey}`);
    const commentsQuery = query(
      commentsRef,
      orderByChild('timestamp'),
      limitToLast(limit)
    );

    // onValue returns an unsubscribe scoped to THIS listener. A bare
    // off(commentsQuery) would detach EVERY listener at the path, so
    // unmounting one subscriber killed all others on the same post.
    const unsubscribe = onValue(commentsQuery, (snapshot) => {
      const comments: Comment[] = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          comments.push({
            id: childSnapshot.key!,
            key: childSnapshot.key!,  // Include key for compatibility
            ...childSnapshot.val()
          });
        });
      }

      // Sort by date ascending (oldest first)
      comments.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      
      callback(comments);
    }, (error) => {
      logger.error('Error in comments subscription:', error);
      callback([]);
    });

    // Register with subscription manager for global cleanup, keyed uniquely
    // per subscription so same-post subscribers coexist
    const subscriptionKey = `comments:${postKey}:${++subscriptionSeq}`;
    subscriptionManager.add(subscriptionKey, unsubscribe);

    // Return unsubscribe function that also removes from manager
    return () => {
      subscriptionManager.remove(subscriptionKey);
    };
  } catch (error) {
    logger.error('Error setting up comments subscription:', error);
    return () => {};
  }
};

// Comment likes removed - not in original app
// /**
//  * Toggles like on a comment
//  * @param postKey The post key
//  * @param commentId The comment ID
//  * @param like True to like, false to unlike
//  */
// export const toggleCommentLike = async (
//   postKey: string,
//   commentId: string,
//   like: boolean
// ): Promise<void> => {
//   try {
//     const user = getCurrentUser();
//     if (!user) {
//       throw new Error('Must be logged in to like comments');
//     }

//     const likePath = `comments/${postKey}/${commentId}/likes/${user.uid}`;
//     const _likeRef = ref(database, likePath);

//     if (like) {
//       await setData(likePath, true);
//     } else {
//       await removeData(likePath);
//     }

//     // Cloud function will handle updating like count
//     logger.info(`[comments.service] Comment like toggled`);
//   } catch (error) {
//     logger.error('Error toggling comment like:', error);
//     throw error;
//   }
// };

/**
 * Gets comment count for a post
 * This is a helper that checks the actual comments node
 * @param postKey The post key
 * @returns Comment count
 */
export const getCommentCount = async (postKey: string): Promise<number> => {
  try {
    const commentsRef = ref(database, `comments/${postKey}`);
    const snapshot = await get(commentsRef);
    
    if (!snapshot.exists()) {
      return 0;
    }

    return snapshot.size || 0;
  } catch (error) {
    logger.error('Error getting comment count:', error);
    return 0;
  }
};

/**
 * Batch get comments for multiple posts
 * Useful for feed rendering
 * @param postKeys Array of post keys
 * @returns Map of postKey to comment count
 */
export const batchGetCommentCounts = async (
  postKeys: string[]
): Promise<Record<string, number>> => {
  try {
    const counts: Record<string, number> = {};
    
    // Fetch counts in parallel
    const promises = postKeys.map(async (postKey) => {
      const count = await getCommentCount(postKey);
      counts[postKey] = count;
    });

    await Promise.all(promises);
    
    return counts;
  } catch (error) {
    logger.error('Error batch getting comment counts:', error);
    return {};
  }
};

// Additional functions for backwards compatibility
export const getReplies = async (
  postKey: string,
  parentCommentId: string
): Promise<Comment[]> => {
  // In the simplified version, replies are part of the main comments
  // Filter comments that are replies to the specified parent
  const comments = await getComments(postKey);
  return comments.filter(comment => 
    (comment as any).replyTo === parentCommentId || 
    (comment as any).parentId === parentCommentId
  );
};

// Comment likes removed - not in original app
// export const toggleLikeComment = async (
//   postKey: string,
//   commentId: string
// ): Promise<void> => {
//   try {
//     const user = getCurrentUser();
//     if (!user) {
//       logger.error('[toggleLikeComment] No authenticated user');
//       throw new Error('Must be logged in');
//     }
//     
//     logger.info(`[toggleLikeComment] Toggling like for comment ${commentId} on post ${postKey}`);
//     
//     const commentPath = `comments/${postKey}/${commentId}`;
//     const commentRef = ref(database, commentPath);
//     
//     // Add retry logic for permission errors
//     let snapshot;
//     try {
//       snapshot = await get(commentRef);
//     } catch (error: any) {
//       if (error?.code === 'PERMISSION_DENIED' || error?.message?.includes('Permission denied')) {
//         logger.warn('[toggleLikeComment] Permission denied on first attempt, retrying...');
//         // Wait for auth state to propagate
//         await new Promise(resolve => setTimeout(resolve, 500));
//         try {
//           snapshot = await get(commentRef);
//         } catch (retryError) {
//           logger.error('[toggleLikeComment] Retry failed:', retryError);
//           throw new Error('Unable to access comment. Please try again.');
//         }
//       } else {
//         throw error;
//       }
//     }
//     
//     if (!snapshot.exists()) {
//       logger.error(`[toggleLikeComment] Comment not found at path: ${commentPath}`);
//       throw new Error('Comment not found');
//     }
//     
//     const comment = snapshot.val();
//     const likes = comment.likes || {};
//     const wasLiked = !!likes[user.uid];
//     
//     if (wasLiked) {
//       // Unlike
//       delete likes[user.uid];
//       logger.info(`[toggleLikeComment] Unliking comment`);
//     } else {
//       // Like
//       likes[user.uid] = true;
//       logger.info(`[toggleLikeComment] Liking comment`);
//     }
//     
//     // Use safe update to avoid root permission errors
//     try {
//       await updateData(commentPath, {
//         likes,
//         likesCount: Object.keys(likes).length
//       });
//       logger.info(`[toggleLikeComment] Successfully ${wasLiked ? 'unliked' : 'liked'} comment`);
//     } catch (updateError: any) {
//       logger.error('[toggleLikeComment] Error updating comment:', updateError);
//       // Try a simpler approach - just update the like status
//       if (wasLiked) {
//         await removeData(`${commentPath}/likes/${user.uid}`);
//       } else {
//         await setData(`${commentPath}/likes/${user.uid}`, true);
//       }
//       // Update count separately
//       await updateData(commentPath, {
//         likesCount: wasLiked ? Math.max(0, Object.keys(likes).length - 1) : Object.keys(likes).length + 1
//       });
//     }
//   } catch (error) {
//     logger.error('[toggleLikeComment] Fatal error:', error);
//     throw error;
//   }
// };

// Comment likes removed - not in original app
// // Additional helper functions for hooks compatibility
// export const hasUserLikedComment = async (
//   postKey: string,
//   commentId: string
// ): Promise<boolean> => {
//   const user = getCurrentUser();
//   if (!user) return false;
//   
//   const likeRef = ref(database, `comments/${postKey}/${commentId}/likes/${user.uid}`);
//   const snapshot = await get(likeRef);
//   return snapshot.exists();
// };

export const subscribeToReplies = (
  postKey: string,
  parentCommentId: string,
  callback: (replies: Comment[]) => void
): (() => void) => {
  // In the simplified version, replies are part of main comments
  // Subscribe to all comments and filter
  return subscribeToComments(postKey, (comments) => {
    const replies = comments.filter(comment => 
      (comment as any).replyTo === parentCommentId || 
      (comment as any).parentId === parentCommentId
    );
    callback(replies);
  });
};
